import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type Row = Record<string, any>;
/**
 * The core snapshot (GET /api/workspace) after expansion: what every screen
 * needs, bounded by the catalog, never by history. History lists (sales,
 * movements, audit, actions, invoices, count observations) come from paged
 * endpoints through the hooks below. See docs/features/performance.md.
 */
export type Venue = {
  id: string;
  readableId: string;
  sequence: number;
  name: string;
  city: string;
  venueType: string;
  timezone: string | null;
  cutoffHour: number | null;
  tags: string[];
  archivedAt: string | null;
  createdAt: string;
};
export type Snapshot = {
  workspace: Row;
  user: Row;
  role: string;
  /** The company (tenant): readable ID and company-wide settings. */
  company: { id: string; readableId: string | null; name: string; productApproval: boolean };
  /** The venue this session works in, and the venues this person may open. */
  venue: Venue;
  venues: Venue[];
  venueScope: "all" | string[];
  /** Other companies this person belongs to (names and IDs only). */
  companies: { id: string; readableId: string | null; name: string; role: string; isDemo: boolean }[];
  /** Present in a Parlyst staff member's read-only support view. */
  support?: { grantId: string; expiresAt: string };
  areas: Row[];
  /** Slim catalog rows (see CORE product columns); full records via /products. */
  products: Row[];
  /** {productId, areaId, quantity} rows expanded from the compact wire shape. */
  stock: Row[];
  /** Count headers only (no observations): open counts and the most recent finalized ones. */
  counts: Row[];
  /** How many finalized counts exist, and how many of them are in `counts` (see useCountHeaders). */
  countHistory?: { finalized: number; inCore: number };
  /**
   * Current, unarchived recipes by kind (null for counters). The recipes
   * themselves are not in the core: see useRecipeIndex and /recipes/list.
   */
  recipeCounts?: { serve: number; made: number } | null;
  /** Open orders as headers (no lines): see /ordering/orders for the lists. */
  openOrders?: Row[];
  visits: Row[];
  members: Row[];
  connections: Row[];
  /** Number of open action items (cached server side). */
  openActions: number;
  /** True while a background recompute of the reminders is pending (openActions is the last computed value). */
  actionsStale?: boolean;
  salesSummary: { rows: number; lastImportedAt?: string } | null;
  /** Signed-in user is a Parlyst platform admin (PARLYST_ADMIN_EMAILS). */
  platformAdmin?: boolean;
  /** The in-app assistant's name in this company: Sam unless an owner renamed it. */
  assistantName?: string;
  /** Keys contributed by server feature modules (see server features/). */
  [feature: string]: any;
};
export type Page<T = Row> = { items: T[]; nextCursor: string | null };
export class ApiError extends Error {
  status: number;
  code?: string;
  /** Structured details some errors carry (e.g. the existing product of a duplicate). */
  details?: Record<string, any>;
  constructor(message: string, status: number, code?: string, details?: Record<string, any>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}
/* ------------------------------------------------------------------ */
/* Current venue: lives in the page address (?venue=) so bookmarks and   */
/* two tabs for two bars both work; sent with every request.            */
/* ------------------------------------------------------------------ */

const VENUE_PARAM = "venue";
let currentVenue: string = (() => {
  try {
    return new URL(location.href).searchParams.get(VENUE_PARAM) ?? "";
  } catch {
    return "";
  }
})();
export const currentVenueId = () => currentVenue;
/** Selects the venue for this tab (empty = the server's default) and writes it to the address bar. */
export function setCurrentVenue(venueId: string) {
  currentVenue = venueId;
  try {
    const url = new URL(location.href);
    if (venueId) url.searchParams.set(VENUE_PARAM, venueId);
    else url.searchParams.delete(VENUE_PARAM);
    history.replaceState(history.state, "", url.pathname + url.search + url.hash);
  } catch {
    /* address bar unavailable */
  }
}
export async function api<T = any>(
  path: string,
  body?: unknown,
  method?: string,
): Promise<T> {
  const response = await fetch("/api" + path, {
    method: method || (body === undefined ? "GET" : "POST"),
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(currentVenue ? { "X-Parlyst-Venue": currentVenue } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const text = await response.text();
  let data: any;
  try {
    data = JSON.parse(text);
  } catch {
    data = { error: text || `Parlyst did not answer (error ${response.status}). Check your connection and try again.` };
  }
  if (!response.ok)
    throw new ApiError(
      data.error || data.message || "That did not work. Try again in a moment.",
      response.status,
      data.code,
      data.details,
    );
  return data;
}
/** Expands the compact wire shapes of the core snapshot. */
export function expandCore(raw: any): Snapshot {
  const { catalog, stock, ...rest } = raw;
  const products: Row[] = catalog
    ? catalog.rows.map((row: any[]) => {
        // Catalog v2: repeated words travel once in `dict` (rows carry the
        // index) and `flags` columns travel as 1/0.
        const p: Row = Object.fromEntries(
          catalog.columns.map((c: string, i: number) => {
            const v = row[i];
            if (v === null || v === undefined) return [c, v];
            if (catalog.dict?.[c]) return [c, catalog.dict[c][v]];
            if (catalog.flags?.includes(c)) return [c, v === 1];
            return [c, v];
          }),
        );
        // The core sends a short photo version instead of the URL (size budget).
        if ("photo" in p) {
          p.photoUrl = p.photo ? `/api/products/${p.id}/photo?v=${p.photo}` : null;
          delete p.photo;
        }
        return p;
      })
    : (rest.products ?? []);
  const rows: Row[] = [];
  if (stock && !Array.isArray(stock))
    for (const [productId, quantities] of Object.entries(
      stock as Record<string, (number | null)[]>,
    ))
      quantities.forEach((quantity, i) => {
        if (quantity !== null && rest.areas?.[i])
          rows.push({ productId, areaId: rest.areas[i].id, quantity });
      });
  return {
    ...rest,
    products,
    stock: Array.isArray(stock) ? stock : rows,
    openActions: Number(rest.openActions) || 0,
  } as Snapshot;
}
export const loadCore = async () => {
  let raw: any;
  try {
    raw = await api("/workspace?catalog=v2");
  } catch (e) {
    // A venue in the address that this company does not have (a tab left
    // on another account's bar, a bookmark of a venue this person lost)
    // falls back to the server's default instead of failing the sign-in.
    const code = e instanceof ApiError ? e.code : undefined;
    if (!currentVenue || (code !== "VENUE_NOT_FOUND" && code !== "VENUE_FORBIDDEN")) throw e;
    setCurrentVenue("");
    raw = await api("/workspace?catalog=v2");
  }
  const core = expandCore(raw);
  // Keep the address bar on the venue the server actually resolved.
  if (core.venue && core.venue.id !== currentVenue) setCurrentVenue(core.venue.id);
  return core;
};

/* ------------------------------------------------------------------ */
/* Refresh bus: mutations tell data hooks that records changed.        */
/* ------------------------------------------------------------------ */

type Invalidation = { prefixes?: string[] };
const listeners = new Set<(inv: Invalidation) => void>();
/**
 * Tells mounted data hooks that records changed: every hook, or only those
 * whose path starts with one of `prefixes` (e.g. the queries of one count
 * after a count entry, so a save refetches a few kilobytes, not every list).
 */
export function invalidate(prefixes?: string[]) {
  for (const l of [...listeners]) l({ prefixes });
}
const pendingInvalidations = new Map<string, ReturnType<typeof setTimeout>>();
/** On sign-out: drop scheduled refetches (the pages holding query data unmount with the signed-in shell). */
export function clearClientCache() {
  for (const timer of pendingInvalidations.values()) clearTimeout(timer);
  pendingInvalidations.clear();
}
/** Debounced invalidate: a burst of changes refetches once, `ms` after the last. */
export function invalidateLater(prefixes: string[], ms: number) {
  const key = prefixes.join("|");
  clearTimeout(pendingInvalidations.get(key));
  pendingInvalidations.set(
    key,
    setTimeout(() => {
      pendingInvalidations.delete(key);
      invalidate(prefixes);
    }, ms),
  );
}
const affects = (path: string | null, inv: Invalidation) =>
  !!path && (!inv.prefixes || inv.prefixes.some((p) => path.startsWith(p)));
/** A counter that changes whenever an invalidation covers `path`. */
function useInvalidation(path: string | null) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const listener = (inv: Invalidation) => {
      if (affects(path, inv)) setN((x) => x + 1);
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [path]);
  return n;
}
/** Mounted useQuery results by path, so a mutation can patch one in place. */
const patchers = new Map<string, Set<(fn: (data: any) => any) => void>>();
/** Applies `fn` to the data of every mounted useQuery for exactly `path`; false when none is mounted. */
export function patchQuery<T = any>(path: string, fn: (data: T) => T): boolean {
  const set = patchers.get(path);
  if (!set?.size) return false;
  for (const p of set) p(fn);
  return true;
}

/** Applies `fn` to every mounted useQuery whose path starts with `prefix` (it gets that path too). */
export function patchQueries<T = any>(prefix: string, fn: (data: T, path: string) => T) {
  for (const [path, set] of patchers) if (path.startsWith(prefix)) for (const p of set) p((data: T) => fn(data, path));
}

export type QueryState<T> = {
  data: T | null;
  loading: boolean;
  error: string;
  /** True when an error occurred and `data` is the last good result. */
  stale: boolean;
  reload: () => Promise<void>;
};
/**
 * Fetches one GET endpoint. A failed fetch keeps the previous result and
 * marks it stale (never an empty dataset); `path` null disables the fetch.
 */
export function useQuery<T = any>(path: string | null, deps: any[] = []): QueryState<T> {
  const dataVersion = useInvalidation(path);
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: string; stale: boolean }>({
    data: null,
    loading: !!path,
    error: "",
    stale: false,
  });
  const latest = useRef(0);
  const run = useCallback(async () => {
    if (!path) {
      setState({ data: null, loading: false, error: "", stale: false });
      return;
    }
    const ticket = ++latest.current;
    setState((s) => ({ ...s, loading: true }));
    try {
      const value = await api<T>(path);
      if (ticket === latest.current) setState({ data: value, loading: false, error: "", stale: false });
    } catch (e) {
      if (ticket === latest.current)
        setState((s) => ({ data: s.data, loading: false, error: (e as Error).message, stale: s.data !== null }));
    }
  }, [path, ...deps]);
  useEffect(() => {
    run();
  }, [run, dataVersion]);
  useEffect(() => {
    if (!path) return;
    const patch = (fn: (data: any) => any) => setState((s) => (s.data === null ? s : { ...s, data: fn(s.data) }));
    const set = patchers.get(path) ?? new Set();
    set.add(patch);
    patchers.set(path, set);
    return () => {
      set.delete(patch);
      if (!set.size) patchers.delete(path);
    };
  }, [path]);
  return { ...state, reload: run };
}

export type PagedState<T> = {
  items: T[];
  loading: boolean;
  error: string;
  stale: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  reload: () => Promise<void>;
};
const withParams = (path: string, params: Record<string, any>) => {
  const url = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== "") url.set(k, String(v));
  const query = url.toString();
  return query ? `${path}${path.includes("?") ? "&" : "?"}${query}` : path;
};
/**
 * Cursor-paged list. The first page reloads whenever records change (keeping
 * the old items visible as stale while it does); `loadMore` appends.
 */
export function usePaged<T = Row>(
  path: string | null,
  params: Record<string, any> = {},
  limit = 50,
): PagedState<T> {
  const key = path ? withParams(path, { ...params, limit }) : null;
  const dataVersion = useInvalidation(key);
  const [state, setState] = useState<{ items: T[]; loading: boolean; error: string; stale: boolean; next: string | null }>({
    items: [],
    loading: !!key,
    error: "",
    stale: false,
    next: null,
  });
  const latest = useRef(0);
  const fetchPage = useCallback(
    async (cursor: string | null) => {
      if (!key) {
        setState({ items: [], loading: false, error: "", stale: false, next: null });
        return;
      }
      const ticket = ++latest.current;
      setState((s) => ({ ...s, loading: true }));
      try {
        const page = await api<Page<T>>(withParams(key, { cursor }));
        if (ticket !== latest.current) return;
        setState((s) => ({
          items: cursor ? [...s.items, ...page.items] : page.items,
          loading: false,
          error: "",
          stale: false,
          next: page.nextCursor,
        }));
      } catch (e) {
        if (ticket === latest.current)
          setState((s) => ({ ...s, loading: false, error: (e as Error).message, stale: s.items.length > 0 }));
      }
    },
    [key],
  );
  useEffect(() => {
    fetchPage(null);
  }, [fetchPage, dataVersion]);
  return {
    items: state.items,
    loading: state.loading,
    error: state.error,
    stale: state.stale,
    hasMore: !!state.next,
    loadMore: () => fetchPage(state.next),
    reload: () => fetchPage(null),
  };
}
/** Full product records (bottle details, calibration numbers) for product pages. */
export function useFullProducts() {
  const q = useQuery<{ items: Row[] }>("/products");
  return { ...q, products: q.data?.items ?? null };
}

export const money = (cents: number | null | undefined) =>
  cents == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(cents / 100);
export const number = (n: number | null | undefined, digits = 2) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-US", { maximumFractionDigits: digits }).format(
        n,
      );
export const date = (value: string | undefined) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "—";
export const uid = () => crypto.randomUUID();
export const now = () => new Date().toISOString();
export const productName = (s: Snapshot, id: string) =>
  s.products.find((p) => p.id === id)?.name || "Archived product";
export const areaName = (s: Snapshot, id?: string) =>
  s.areas.find((a) => a.id === id)?.name || "—";
export function download(name: string, content: string, type = "text/plain") {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ------------------------------------------------------------------ */
/* Count detail, kept current by deltas                                */
/* ------------------------------------------------------------------ */

/** Merges changed observations and revisions (by id) into a count detail. */
export function mergeCountChanges(current: Row, changes: Row): Row {
  const { delta: _d, since: _s, observations = [], revisions = [], ...header } = changes;
  const byId = new Map<string, Row>((current.observations ?? []).map((o: Row) => [o.id, o]));
  for (const o of observations) byId.set(o.id, o);
  const revKey = (r: Row) => `${r.id}:${r.version}`;
  const revs = new Map<string, Row>((current.revisions ?? []).map((r: Row) => [revKey(r), r]));
  for (const r of revisions) revs.set(revKey(r), r);
  return { ...current, ...header, observations: [...byId.values()], revisions: [...revs.values()] };
}
/**
 * GET /api/counts/:id for the count being counted. The first load is the
 * full detail; afterwards any change to this count (invalidate(["/counts/<id>"]))
 * fetches only what was saved since (`?since=<version>`) and merges it, so a
 * count entry costs a few kilobytes however large the count is. A mutation
 * can also patch it in place (patchQuery).
 */
export function useCountDetail(countId: string | null): QueryState<Row> {
  const path = countId ? `/counts/${countId}` : null;
  const changed = useInvalidation(path);
  const [state, setState] = useState<{ data: Row | null; loading: boolean; error: string; stale: boolean }>({
    data: null,
    loading: !!path,
    error: "",
    stale: false,
  });
  const current = useRef<Row | null>(null),
    latest = useRef(0);
  const set = (data: Row | null, error = "") => {
    current.current = data;
    setState({ data, loading: false, error, stale: false });
  };
  const full = useCallback(async () => {
    if (!path) {
      set(null);
      return;
    }
    const ticket = ++latest.current;
    setState((s) => ({ ...s, loading: true }));
    try {
      const value = await api<Row>(path);
      if (ticket === latest.current) set(value);
    } catch (e) {
      if (ticket === latest.current)
        setState((s) => ({ data: s.data, loading: false, error: (e as Error).message, stale: s.data !== null }));
    }
  }, [path]);
  const delta = useCallback(async () => {
    const base = current.current;
    if (!path || !base || !base.version) return full();
    const ticket = ++latest.current;
    try {
      const changes = await api<Row>(`${path}?since=${encodeURIComponent(base.version)}`);
      if (ticket !== latest.current) return;
      const merged = mergeCountChanges(current.current ?? base, changes);
      // Something was removed (or hidden): the delta cannot say what, so reload.
      if (!merged.blindRedacted && merged.observations.length !== changes.observationCount) return full();
      set(merged);
    } catch (e) {
      if (ticket === latest.current) setState((s) => ({ ...s, loading: false, error: (e as Error).message, stale: s.data !== null }));
    }
  }, [path, full]);
  useEffect(() => {
    current.current = null;
    full();
  }, [full]);
  useEffect(() => {
    if (changed) delta();
  }, [changed]);
  useEffect(() => {
    if (!path) return;
    const patch = (fn: (data: any) => any) => {
      if (!current.current) return;
      set(fn(current.current));
    };
    const s = patchers.get(path) ?? new Set();
    s.add(patch);
    patchers.set(path, s);
    return () => {
      s.delete(patch);
      if (!s.size) patchers.delete(path);
    };
  }, [path]);
  return { ...state, reload: full };
}

/**
 * Starts a server-sent download (Content-Disposition: attachment) as a plain
 * link, so a large file streams straight to disk instead of through fetch and
 * JSON parsing in the page. The venue travels in the address (?venue=).
 */
export function downloadLink(path: string) {
  const url = new URL("/api" + path, location.origin);
  if (currentVenue) url.searchParams.set(VENUE_PARAM, currentVenue);
  const a = document.createElement("a");
  a.href = url.pathname + url.search;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
/** The complete workspace export (NDJSON, streamed by the server). */
export const downloadExport = () => downloadLink("/export");

/**
 * Every count header: the core carries open counts and the most recent
 * finalized ones (`countHistory` says how many exist); older finalized headers
 * load from GET /api/counts only where a screen needs them (report pickers,
 * the full list of counts).
 */
export function useCountHeaders(s: Snapshot): Row[] {
  const more = !!s.countHistory && s.countHistory.finalized > s.countHistory.inCore;
  const q = useQuery<{ items: Row[] }>(more ? "/counts?status=finalized" : null);
  if (!more || !q.data) return s.counts;
  const seen = new Set(s.counts.map((c) => c.id));
  return [...s.counts, ...q.data.items.filter((c) => !seen.has(c.id))].sort((x, y) => y.startedAt.localeCompare(x.startedAt));
}

/* ------------------------------------------------------------------ */
/* Lists that are not in the core: they grow with the menu, the shelves  */
/* and the supplier catalogs, so screens load them on demand.            */
/* ------------------------------------------------------------------ */

/** A current recipe as GET /api/recipes/current lists it (the whole record with `full`). */
export type RecipeIndexRow = {
  id: string;
  name: string;
  kind: "serve" | "prep" | "batch";
  yieldMl: number | null;
  yieldQuantity: number | null;
  yieldUnit: string | null;
  outputProductId: string | null;
  sellingPriceCents: number | null;
  archivedAt: string | null;
  /** With `full`: the rest of the record (ingredients, version, ...). */
  ingredients?: Row[];
  [field: string]: any;
};
/**
 * The company's current recipes for pickers and name lookups (the recipes
 * table pages through /recipes/list instead). `recipes` is null while loading.
 */
export function useRecipeIndex(options: { full?: boolean; enabled?: boolean } = {}) {
  const q = useQuery<{ items: RecipeIndexRow[] }>(options.enabled === false ? null : `/recipes/current${options.full ? "?full=1" : ""}`);
  return { ...q, recipes: q.data?.items ?? null };
}

/** One bottle's spot on a shelf. */
export type Placement = { productId: string; areaId: string; shelfId: string; position: number };
/** Per-shelf bottle lists (the placements endpoint and the count bundle) as placement rows. */
export function expandPlacements(shelves: { id: string; areaId: string; productIds?: string[] }[] | null | undefined): Placement[] {
  return (shelves ?? []).flatMap((s) => (s.productIds ?? []).map((productId, position) => ({ productId, areaId: s.areaId, shelfId: s.id, position })));
}
/**
 * The shelf placements of some areas (GET /counting/placements). Null
 * `areaIds` disables the fetch; an empty list means every area of this bar.
 */
export function usePlacements(areaIds: string[] | null) {
  const key = areaIds ? [...new Set(areaIds)].sort().join(",") : null;
  const q = useQuery<{ shelves: { id: string; areaId: string; productIds: string[] }[] }>(
    key === null ? null : `/counting/placements${key ? `?areaIds=${encodeURIComponent(key)}` : ""}`,
  );
  const placements = useMemo(() => (q.data ? expandPlacements(q.data.shelves) : null), [q.data]);
  return { ...q, placements };
}

/** One supplier's items (price quotes), loaded when a screen needs them; a null supplier disables it. */
export function useSupplierItems(supplierId: string | null | undefined) {
  const q = useQuery<{ items: Row[] }>(supplierId ? `/ordering/supplier-items?supplierId=${encodeURIComponent(supplierId)}` : null);
  return { ...q, items: q.data?.items ?? null };
}
